import onReady from "./modules/onReady.min.js";
import slugify from "./modules/slugify.min.js";
import toast from "./modules/toast.min.js";

onReady(() => {
  const form = document.getElementById("get-leads-form");
  if (!form) {
    return;
  }

  const viewLeadsBtn = document.getElementById("view-results-btn");
  const downloadCSVBtn = document.getElementById("download-csv");
  const resultsWrapper = document.getElementById("results-wrapper");
  const resultsCount = document.getElementById("results-count");
  const loaderTable = document.getElementById("loader-table");
  const shareLeadsBtn = document.getElementById("share-leads");
  const shareDialog = document.getElementById("share-dialog");
  const shareableUrlInput = document.getElementById("shareableUrl");
  const copyUrlBtn = document.getElementById("copyUrlBtn");
  const postUrlInput = document.getElementById("post-url-input");
  const filterInput = document.getElementById("results-filter");
  const filterClearBtn = document.getElementById("results-filter-clear");

  let table = null;
  let exportFileName = "pymnts-leads";
  let totalRows = 0;

  const leads = (n) => `${n.toLocaleString()} lead${n === 1 ? "" : "s"}`;

  const downloadFilteredBtn = document.getElementById("download-filtered");
  const downloadFilteredLabel = document.getElementById("download-filtered-label");

  const showCount = (shown) => {
    const filtered = filterInput.value.trim() !== "";
    resultsCount.textContent = filtered ? `${shown.toLocaleString()} of ${leads(totalRows)}` : leads(totalRows);
    downloadFilteredBtn.hidden = !filtered;
    downloadFilteredBtn.disabled = shown === 0;
    downloadFilteredLabel.textContent = `Download ${shown.toLocaleString()} filtered`;
  };

  /** Rows where any column contains the filter text, ignoring case. */
  const applyFilter = () => {
    const query = filterInput.value.trim().toLowerCase();
    filterClearBtn.hidden = query === "";
    if (!table) return;
    if (!query) {
      table.clearFilter();
      return;
    }
    table.setFilter((row) => Object.values(row).some((value) => String(value ?? "").toLowerCase().includes(query)));
  };

  let filterTimer;
  filterInput.addEventListener("input", () => {
    clearTimeout(filterTimer);
    filterTimer = setTimeout(applyFilter, 150);
  });
  filterInput.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && filterInput.value) {
      filterInput.value = "";
      applyFilter();
    }
  });
  filterClearBtn.addEventListener("click", () => {
    filterInput.value = "";
    applyFilter();
    filterInput.focus();
  });

  const handleUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get("search");
    const fieldsParam = urlParams.getAll("fields");

    if (!searchParam) return;
    postUrlInput.value = searchParam;

    if (fieldsParam.length > 0) {
      document.querySelectorAll('input[name="fields[]"]').forEach((cb) => {
        cb.checked = fieldsParam.includes(cb.value);
      });
    }

    form.requestSubmit();
  };

  copyUrlBtn.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(shareableUrlInput.value);
    } catch {
      shareableUrlInput.select();
      document.execCommand("copy");
    }
    copyUrlBtn.textContent = "Copied";
    setTimeout(() => {
      copyUrlBtn.textContent = "Copy";
    }, 2000);
  });

  /** This search as a query string, which handleUrlParams reads back on load. */
  const searchQuery = () => {
    const urlParams = new URLSearchParams();
    urlParams.set("search", postUrlInput.value.trim());
    document.querySelectorAll('input[name="fields[]"]:checked').forEach((cb) => urlParams.append("fields", cb.value));
    return `?${urlParams.toString()}`;
  };

  shareLeadsBtn.addEventListener("click", () => {
    shareableUrlInput.value = `${window.location.origin}${window.location.pathname}${searchQuery()}`;
    shareDialog.showModal();
    shareableUrlInput.select();
  });

  // Without a BOM, Excel decodes the UTF-8 file as Mac Roman/Windows-1252.
  downloadCSVBtn.addEventListener("click", () => {
    table?.download("csv", `${exportFileName}.csv`, { bom: true }, "all");
  });
  downloadFilteredBtn.addEventListener("click", () => {
    table?.download("csv", `${exportFileName}-filtered.csv`, { bom: true }, "active");
  });

  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    // Kept in the address so a refresh brings the same search back.
    history.replaceState(null, "", `${window.location.pathname}${searchQuery()}`);
    // The previous results go before the new ones load, so a pending filter can't act on them.
    clearTimeout(filterTimer);
    filterInput.value = "";
    filterClearBtn.hidden = true;
    table?.destroy();
    table = null;
    totalRows = 0;
    downloadCSVBtn.disabled = true;
    downloadFilteredBtn.hidden = true;
    shareLeadsBtn.disabled = true;
    viewLeadsBtn.disabled = true;
    viewLeadsBtn.textContent = "Loading…";
    resultsCount.textContent = "";
    loaderTable.hidden = false;
    resultsWrapper.hidden = true;

    try {
      const data = new URLSearchParams();
      for (const pair of new FormData(form)) {
        data.append(pair[0], pair[1]);
      }

      const leadsData = await fetch(`/data`, { method: "post", body: data });
      const leadsDataJSON = await leadsData.json().catch(() => ({}));

      if (leadsData.status === 401) {
        window.location.href = `/?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        return;
      }
      if (!leadsData.ok || leadsDataJSON?.error) {
        throw new Error(leadsDataJSON.error || "Server error occurred");
      }
      if (!Array.isArray(leadsDataJSON?.data)) {
        throw new Error("No data received from server");
      }

      exportFileName = slugify(String(leadsDataJSON.pageSlug || leadsDataJSON.formId)) || "pymnts-leads";
      const rows = leadsDataJSON.data;
      totalRows = rows.length;
      filterInput.value = "";
      filterClearBtn.hidden = true;
      showCount(totalRows);

      if (rows.length === 0) {
        toast("No leads found for that page or form.", "info");
        return;
      }

      resultsWrapper.hidden = false;
      table?.destroy();
      table = new Tabulator("#results", {
        data: rows,
        autoColumns: true,
        layout: "fitDataStretch",
        pagination: "local",
        paginationSize: 25,
        paginationSizeSelector: [10, 25, 50, 100],
        paginationCounter: "rows",
      });
      table.on("dataFiltered", (filters, filteredRows) => showCount(filteredRows.length));

      downloadCSVBtn.disabled = false;
      shareLeadsBtn.disabled = false;
    } catch (err) {
      console.error("Error details:", err);
      toast(err.message || "Unable to process your request at this time, sorry!", "error");
    } finally {
      loaderTable.hidden = true;
      viewLeadsBtn.disabled = false;
      viewLeadsBtn.textContent = "View leads";
    }
  });

  handleUrlParams();
});
